// =============================================================================
// TOKENS DE ESPACIADO - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Tokens de espaciado centralizados basados en una escala consistente
// para mantener armonía visual y facilitar el mantenimiento

// Escala base de espaciado (basada en 4px como unidad base)
export const baseSpacing = 4; // 4px

// Espaciado principal (escala de 4px)
export const spacing = {
  0: '0px',
  px: '1px',
  0.5: '2px',   // 0.5 * 4px
  1: '4px',     // 1 * 4px
  1.5: '6px',   // 1.5 * 4px
  2: '8px',     // 2 * 4px
  2.5: '10px',  // 2.5 * 4px
  3: '12px',    // 3 * 4px
  3.5: '14px',  // 3.5 * 4px
  4: '16px',    // 4 * 4px
  5: '20px',    // 5 * 4px
  6: '24px',    // 6 * 4px
  7: '28px',    // 7 * 4px
  8: '32px',    // 8 * 4px
  9: '36px',    // 9 * 4px
  10: '40px',   // 10 * 4px
  11: '44px',   // 11 * 4px
  12: '48px',   // 12 * 4px
  14: '56px',   // 14 * 4px
  16: '64px',   // 16 * 4px
  20: '80px',   // 20 * 4px
  24: '96px',   // 24 * 4px
  28: '112px',  // 28 * 4px
  32: '128px',  // 32 * 4px
  36: '144px',  // 36 * 4px
  40: '160px',  // 40 * 4px
  44: '176px',  // 44 * 4px
  48: '192px',  // 48 * 4px
  52: '208px',  // 52 * 4px
  56: '224px',  // 56 * 4px
  60: '240px',  // 60 * 4px
  64: '256px',  // 64 * 4px
  72: '288px',  // 72 * 4px
  80: '320px',  // 80 * 4px
  96: '384px',  // 96 * 4px
};

// Espaciado semántico para componentes específicos
export const componentSpacing = {
  // Espaciado interno de componentes (padding)
  component: {
    xs: spacing[2],    // 8px
    sm: spacing[3],    // 12px
    md: spacing[4],    // 16px
    lg: spacing[6],    // 24px
    xl: spacing[8],    // 32px
  },

  // Espaciado entre elementos (margin/gap)
  element: {
    xs: spacing[1],    // 4px
    sm: spacing[2],    // 8px
    md: spacing[4],    // 16px
    lg: spacing[6],    // 24px
    xl: spacing[8],    // 32px
  },

  // Espaciado de secciones
  section: {
    xs: spacing[8],    // 32px
    sm: spacing[12],   // 48px
    md: spacing[16],   // 64px
    lg: spacing[20],   // 80px
    xl: spacing[24],   // 96px
  },

  // Espaciado de layout
  layout: {
    xs: spacing[4],    // 16px
    sm: spacing[6],    // 24px
    md: spacing[8],    // 32px
    lg: spacing[12],   // 48px
    xl: spacing[16],   // 64px
  },
};

// Espaciado específico para tipos de componentes
export const specificSpacing = {
  // Botones
  button: {
    padding: {
      xs: `${spacing[1]} ${spacing[2]}`,      // 4px 8px
      sm: `${spacing[2]} ${spacing[3]}`,      // 8px 12px
      md: `${spacing[2.5]} ${spacing[4]}`,    // 10px 16px
      lg: `${spacing[3]} ${spacing[6]}`,      // 12px 24px
      xl: `${spacing[4]} ${spacing[8]}`,      // 16px 32px
    },
    gap: {
      xs: spacing[1],    // 4px
      sm: spacing[2],    // 8px
      md: spacing[2],    // 8px
      lg: spacing[3],    // 12px
      xl: spacing[3],    // 12px
    },
  },

  // Inputs/Formularios
  input: {
    padding: {
      xs: `${spacing[2]} ${spacing[3]}`,      // 8px 12px
      sm: `${spacing[2.5]} ${spacing[3]}`,    // 10px 12px
      md: `${spacing[3]} ${spacing[4]}`,      // 12px 16px
      lg: `${spacing[4]} ${spacing[5]}`,      // 16px 20px
    },
    margin: {
      xs: spacing[2],    // 8px
      sm: spacing[3],    // 12px
      md: spacing[4],    // 16px
      lg: spacing[6],    // 24px
    },
  },

  // Cards
  card: {
    padding: {
      xs: spacing[3],    // 12px
      sm: spacing[4],    // 16px
      md: spacing[6],    // 24px
      lg: spacing[8],    // 32px
    },
    gap: {
      xs: spacing[3],    // 12px
      sm: spacing[4],    // 16px
      md: spacing[6],    // 24px
      lg: spacing[8],    // 32px
    },
  },

  // Modales/Dialogs
  modal: {
    padding: {
      xs: spacing[4],    // 16px
      sm: spacing[6],    // 24px
      md: spacing[8],    // 32px
      lg: spacing[12],   // 48px
    },
    gap: {
      xs: spacing[4],    // 16px
      sm: spacing[6],    // 24px
      md: spacing[8],    // 32px
    },
  },

  // Navegación
  nav: {
    padding: {
      xs: `${spacing[2]} ${spacing[3]}`,      // 8px 12px
      sm: `${spacing[3]} ${spacing[4]}`,      // 12px 16px
      md: `${spacing[4]} ${spacing[6]}`,      // 16px 24px
    },
    gap: {
      xs: spacing[2],    // 8px
      sm: spacing[4],    // 16px
      md: spacing[6],    // 24px
    },
  },

  // Tablas
  table: {
    cellPadding: {
      xs: `${spacing[2]} ${spacing[3]}`,      // 8px 12px
      sm: `${spacing[3]} ${spacing[4]}`,      // 12px 16px
      md: `${spacing[4]} ${spacing[6]}`,      // 16px 24px
    },
    headerPadding: {
      xs: `${spacing[3]} ${spacing[3]}`,      // 12px 12px
      sm: `${spacing[4]} ${spacing[4]}`,      // 16px 16px
      md: `${spacing[5]} ${spacing[6]}`,      // 20px 24px
    },
  },

  // Listas
  list: {
    itemPadding: {
      xs: `${spacing[2]} ${spacing[3]}`,      // 8px 12px
      sm: `${spacing[3]} ${spacing[4]}`,      // 12px 16px
      md: `${spacing[4]} ${spacing[6]}`,      // 16px 24px
    },
    gap: {
      xs: spacing[1],    // 4px
      sm: spacing[2],    // 8px
      md: spacing[3],    // 12px
    },
  },

  // Notificaciones/Alerts
  alert: {
    padding: {
      xs: spacing[3],    // 12px
      sm: spacing[4],    // 16px
      md: spacing[5],    // 20px
    },
    gap: spacing[3],     // 12px
  },

  // Tooltips
  tooltip: {
    padding: `${spacing[2]} ${spacing[3]}`,   // 8px 12px
  },

  // Badges
  badge: {
    padding: {
      xs: `${spacing[0.5]} ${spacing[2]}`,    // 2px 8px
      sm: `${spacing[1]} ${spacing[2.5]}`,    // 4px 10px
      md: `${spacing[1.5]} ${spacing[3]}`,    // 6px 12px
    },
  },
};

// Espaciado para grids y layouts
export const gridSpacing = {
  // Gap entre elementos de grid
  gap: {
    xs: spacing[2],    // 8px
    sm: spacing[4],    // 16px
    md: spacing[6],    // 24px
    lg: spacing[8],    // 32px
    xl: spacing[12],   // 48px
  },

  // Columnas de grid
  column: {
    gap: {
      xs: spacing[3],    // 12px
      sm: spacing[4],    // 16px
      md: spacing[6],    // 24px
      lg: spacing[8],    // 32px
    },
  },

  // Contenedores
  container: {
    padding: {
      xs: spacing[4],    // 16px
      sm: spacing[6],    // 24px
      md: spacing[8],    // 32px
      lg: spacing[12],   // 48px
      xl: spacing[16],   // 64px
    },
  },
};

// Espaciado responsivo
export const responsiveSpacing = {
  // Espaciado que cambia según el breakpoint
  adaptive: {
    xs: {
      padding: spacing[4],
      margin: spacing[2],
      gap: spacing[2],
    },
    sm: {
      padding: spacing[6],
      margin: spacing[4],
      gap: spacing[4],
    },
    md: {
      padding: spacing[8],
      margin: spacing[6],
      gap: spacing[6],
    },
    lg: {
      padding: spacing[12],
      margin: spacing[8],
      gap: spacing[8],
    },
    xl: {
      padding: spacing[16],
      margin: spacing[12],
      gap: spacing[12],
    },
  },
};

// Utilidades de espaciado
export const spacingUtilities = {
  // Función para generar espaciado automático
  auto: 'auto',
  
  // Espaciado negativo (para overlaps controlados)
  negative: Object.fromEntries(
    Object.entries(spacing).map(([key, value]) => [
      key,
      value === '0px' ? '0px' : `-${value}`
    ])
  ),

  // Espaciado centrado
  center: {
    margin: '0 auto',
  },

  // Espaciado para elementos flotantes
  float: {
    clearfix: {
      '&::after': {
        content: '""',
        display: 'table',
        clear: 'both',
      },
    },
  },
};

// Funciones utilitarias
export const createSpacing = (multiplier) => {
  return `${baseSpacing * multiplier}px`;
};

export const createCustomSpacing = (top, right, bottom, left) => {
  const values = [top, right, bottom, left]
    .map(val => typeof val === 'number' ? createSpacing(val) : val)
    .filter(Boolean);
  
  return values.join(' ');
};

// Espaciado para animaciones y transiciones
export const animationSpacing = {
  // Distancias de movimiento para animaciones
  slide: {
    xs: spacing[4],    // 16px
    sm: spacing[8],    // 32px
    md: spacing[16],   // 64px
    lg: spacing[24],   // 96px
  },
  
  // Offsets para efectos hover
  hover: {
    lift: spacing[1],      // 4px
    shift: spacing[0.5],   // 2px
  },
};

// Espaciado específico para el sistema de pagos
export const paymentSpacing = {
  // Formularios de pago
  paymentForm: {
    fieldGap: spacing[4],        // 16px
    sectionGap: spacing[8],      // 32px
    padding: spacing[6],         // 24px
  },
  
  // Cards de método de pago
  paymentMethod: {
    padding: spacing[4],         // 16px
    gap: spacing[3],            // 12px
    margin: spacing[2],         // 8px
  },
  
  // Resúmenes de pago
  paymentSummary: {
    padding: spacing[6],         // 24px
    itemGap: spacing[3],        // 12px
    sectionGap: spacing[6],     // 24px
  },
};

// Exportar todo
const spacingTokens = {
  base: baseSpacing,
  spacing,
  component: componentSpacing,
  specific: specificSpacing,
  grid: gridSpacing,
  responsive: responsiveSpacing,
  utilities: spacingUtilities,
  animation: animationSpacing,
  payment: paymentSpacing,
  createSpacing,
  createCustomSpacing,
};
export default spacingTokens;